import {
  ChakraTheme,
  ComponentStyleConfig,
  theme as chakraTheme,
} from '@chakra-ui/react'
import { getColor } from '@chakra-ui/theme-tools'

import { colors } from '../foundations/colors'

const { variants } = chakraTheme.components.Button

const getBoxShadow = (theme: Partial<ChakraTheme>) =>
  `0px 0px 0px 2px ${getColor(theme, 'white.0')}, 0px 0px 0px 4px ${getColor(
    theme,
    'deepPurple',
  )}`

export const Button: ComponentStyleConfig = {
  baseStyle: {
    _disabled: {
      borderColor: 'neutral.30',
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
    _focus: {
      borderColor: 'transparent',
      boxShadow: getBoxShadow({ colors }),
    },
    borderRadius: 0,
    fontWeight: 400,
    transition: 'none',
  },
  defaultProps: {
    colorScheme: '',
    variant: 'text',
  },
  variants: {
    action: props => ({
      ...variants.solid(props),
      _active: {
        bg: 'blue.60',
      },
      _disabled: {
        bg: 'neutral.30',
      },
      _hover: {
        _disabled: {
          bg: 'neutral.30',
        },
        bg: 'blue.50',
      },
      bgGradient: `linear(to-r, violet.50, blue.50)`,
      color: 'white.0',
    }),
    error: props => ({
      ...variants.outline(props),
      _active: {
        bg: 'red.80',
        borderColor: 'red.80',
        color: 'white.0',
      },
      _disabled: {
        borderColor: 'neutral.30',
        color: 'neutral.30',
      },
      _focus: {
        borderColor: 'red.70',
      },
      _hover: {
        _disabled: {
          borderColor: 'neutral.30',
          color: 'neutral.30',
        },
        bg: 'red.70',
        color: 'white',
      },
      border: '1px solid',
      borderColor: 'red.70',
      color: 'red.70',
    }),
    outline: props => ({
      ...variants.outline(props),
      _active: {
        bg: 'neutral.90',
        borderColor: 'neutral.90',
        color: 'white.0',
      },
      _disabled: {
        borderColor: 'neutral.30',
        color: 'neutral.30',
      },
      _focus: { bg: 'deepPurple', borderColor: 'deepPurple', color: 'white.0' },
      _hover: {
        _disabled: {
          borderColor: 'neutral.30',
          color: 'neutral.30',
        },
        bg: 'neutral.80',
        color: 'white.0',
      },
      border: '1px solid',
      borderColor: 'deepPurple',
      color: 'deepPurple',
    }),
    solid: props => ({
      ...variants.solid(props),
      _active: {
        bg: 'neutral.90',
      },
      _disabled: {
        bg: 'neutral.30',
      },
      _hover: {
        _disabled: {
          bg: 'neutral.30',
        },
        bg: 'neutral.80',
      },
      bg: 'deepPurple',
      color: 'white.0',
    }),
    success: props => ({
      ...variants.outline(props),
      _active: {
        bg: 'green.80',
        borderColor: 'green.80',
        color: 'white.0',
      },
      _disabled: {
        borderColor: 'neutral.30',
        color: 'neutral.30',
      },
      _focus: {
        borderColor: 'green.70',
      },
      _hover: {
        _disabled: {
          borderColor: 'neutral.30',
          color: 'neutral.30',
        },
        bg: 'green.70',
        color: 'white',
      },
      border: '1px solid',
      borderColor: 'green.70',
      color: 'green.70',
    }),
    text: props => ({
      ...variants.link(props),
      _active: {
        color: 'deepPurple',
      },
      _disabled: {
        color: 'neutral.30',
      },
      _focus: {
        border: 0,
        boxShadow: 'none',
        outline: '0px',
        textDecoration: 'underline',
      },
      _hover: {
        color: 'neutral.80',
      },
      color: 'deepPurple',
    }),
  },
}
